import {
  AfterViewInit,
  Component,
  effect,
  inject,
  Input,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Dialog } from '@angular/cdk/dialog';
import { TaskService } from '@modules/sales/services/task.service';
import { Task } from '@modules/sales/models/task.model';
import { ISettingsPage } from '@modules/settings/services/kfm-settings-token';
import { KfmSpinnerDialogComponent } from '@shared/ui/components/features/dialogs/spinner-dialog/kfm-spinner-dialog.component';

// UI Imports
import { HsLayerModule } from '@hsetu/ngui-resources';
import {
  HsButtonModule,
  HsIconModule,
  HsPageHeaderModule,
  HsTooltipModule,
} from '@hsetu/ngui-components';
import { KfmSalesDetailsComponent } from '../../sales/sales-details/kfm-sales-details.component';
import { ICON_CONVERT } from '@shared/ui/icons/icon-constants';
import { KfmYesNoDialogComponent } from '@shared/ui/components/features/dialogs/yes-no-dialog/kfm-yes-no-dialog.component';

@Component({
  selector: 'kfm-sales-settings',
  standalone: true,
  imports: [
    KfmSalesDetailsComponent,
    HsLayerModule,
    HsIconModule,
    HsPageHeaderModule,
    HsTooltipModule,
    HsButtonModule,
  ],
  templateUrl: './kfm-sales-settings.component.html',
})
export class KfmSalesSettingsComponent {
  private readonly _dialog = inject(Dialog);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _taskService = inject(TaskService);
  public tooltipReset = input<string>('');
  protected readonly iconReset = this.getSafeIcon(ICON_CONVERT);

  @Input() set config(conf: ISettingsPage) {
    if (conf) {
      this.pageConfig.set(conf);
      const id = conf.initialData?.templateId || (conf as any).templateId;
      if (id) {
        this.loadTask(id);
      }
    }
  }

  protected pageConfig = signal<ISettingsPage | undefined>(undefined);
  protected templateTask = signal<Task | undefined>(undefined);

  private async loadTask(id: number) {
    const dialogRef = this._dialog.open(KfmSpinnerDialogComponent, {
      data: { text: this.pageConfig()?.initialData?.loadText },
    });

    try {
      const task = await this._taskService.getTemplateTask(id);
      this.templateTask.set(task);
    } catch (error) {
      console.error('Fehler beim Laden', error);
    } finally {
      dialogRef.close();
    }
  }

  getSafeIcon(icon: string | undefined) {
    return icon ? this._sanitizer.bypassSecurityTrustHtml(icon) : '';
  }

  protected async resetTemplate() {
    const id = this.pageConfig()?.initialData?.templateId;
    if (!id) return;

    const dialogRef = this._dialog.open(KfmYesNoDialogComponent, {
      data: {
        title: 'Standardeinstellungen zurücksetzen',
        text: 'Möchten Sie die Standardeinstellungen wirklich zurücksetzen? Alle aktuellen Einstellungen gehen dabei verloren.',
      },
    });

    dialogRef.closed.subscribe(async (proceed) => {
      if (proceed) {
        const spinnerDialogRef = this._dialog.open(KfmSpinnerDialogComponent, {
          data: { text: 'Standardeinstellungen werden geladen...' },
        });

        try {
          const task = await this._taskService.resetTemplate(id);
          this.templateTask.set(task);
        } catch (error) {
          console.error('Fehler beim Zurücksetzen', error);
        } finally {
          spinnerDialogRef.close();
        }
      }
    });
  }
}
